import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, UserPlus, ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthParam = () => {
  const token = localStorage.getItem('c3i_token');
  return token ? `Bearer ${token}` : '';
};

export default function NominaDelegatiPage() {
  const { user, isAdminOrdine } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const preselectedTipo = searchParams.get('tipo') || '';
  
  const [formData, setFormData] = useState({
    tipo: preselectedTipo,
    nome: '',
    cognome: '',
    email: '',
    cellulare: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  useEffect(() => {
    if (!isAdminOrdine()) {
      navigate('/dashboard');
    }
  }, [isAdminOrdine, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.tipo || !formData.nome || !formData.cognome || !formData.email || !formData.cellulare) {
      toast.error('Tutti i campi sono obbligatori');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/deleghe/nomina?Authorization=${encodeURIComponent(getAuthParam())}`,
        formData
      );
      
      setSubmittedData({
        tipo: formData.tipo,
        tipo_label: response.data.tipo_label,
        nome: formData.nome,
        cognome: formData.cognome,
        richiesta_id: response.data.richiesta_id
      });
      setSubmitted(true);
      toast.success('Richiesta generata correttamente');
    } catch (error) {
      console.error('Error submitting nomination:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio della richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (submittedData?.richiesta_id) {
      try {
        const url = `${API}/deleghe/nomina/${submittedData.richiesta_id}/pdf?Authorization=${encodeURIComponent(getAuthParam())}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Errore durante il download del PDF');
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `Nomina_C3I_${submittedData.cognome}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error downloading PDF:', error);
        toast.error('Errore durante il download del PDF');
      }
    }
  };

  // Success page after submission
  if (submitted) {
    return (
      <div className="p-6 lg:p-8" data-testid="nomina-success-page">
        <div className="max-w-xl mx-auto">
          <Card className="card-institutional">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                Nomina generata correttamente
              </h2>
              <p className="text-slate-600 mb-6">
                La nomina deve essere ora scaricata, firmata digitalmente e inviata via PEC a{' '}
                <a href="mailto:segreteria@ingpec.eu" className="text-blue-600 hover:underline">
                  segreteria@ingpec.eu
                </a>
              </p>
              {submittedData && (
                <div className="p-4 bg-slate-50 rounded-lg mb-6 text-left">
                  <p className="text-sm text-slate-600">
                    <strong>Tipo:</strong> {submittedData.tipo_label || (submittedData.tipo === 'DELEGATO' ? 'Delegato C3i' : 'Delegato Supplente')}
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>Nominato:</strong> {submittedData.nome} {submittedData.cognome}
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Button 
                  onClick={handleDownloadPDF} 
                  className="w-full"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica PDF della richiesta
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')} 
                  className="w-full"
                  data-testid="back-to-dashboard-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Torna alla Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="nomina-delegati-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
        </div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-4">Nomina Delegati</h1>
        <p className="text-slate-500 mt-1">Invia nomina Delegato C3i o Supplente</p>
      </div>

      <div className="max-w-xl mx-auto">
        <Card className="card-institutional">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-slate-600" />
              Dati del Nominato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Tipo */}
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(v) => handleChange('tipo', v)}
                >
                  <SelectTrigger data-testid="select-tipo">
                    <SelectValue placeholder="Seleziona tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DELEGATO">DELEGATO</SelectItem>
                    <SelectItem value="SUPPLENTE">SUPPLENTE</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  placeholder="Mario"
                  data-testid="input-nome"
                />
              </div>

              {/* Cognome */}
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => handleChange('cognome', e.target.value)}
                  placeholder="Rossi"
                  data-testid="input-cognome"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="mario.rossi@email.it"
                  data-testid="input-email"
                />
              </div>

              {/* Cellulare */}
              <div className="space-y-2">
                <Label htmlFor="cellulare">Cellulare *</Label>
                <Input
                  id="cellulare"
                  type="tel"
                  value={formData.cellulare}
                  onChange={(e) => handleChange('cellulare', e.target.value)}
                  placeholder="+39 333 1234567"
                  data-testid="input-cellulare"
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting}
                data-testid="submit-nomina-btn"
              >
                {submitting ? 'Invio in corso...' : 'INVIO'}
              </Button>

              <p className="text-xs text-slate-500 text-center">* Campi obbligatori</p>
            </form>

            {/* Nota */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>NOTA</strong> - Si ricorda che ogni Ordine può nominare un solo Delegato e un solo Supplente 
                e che ogni invio di un nuovo nominativo per ognuno dei ruoli sostituisce il precedente a far data 
                dalla ratifica nel primo Consiglio C3i utile.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
